(() => {
  // Configuración de debug - cambiar a true solo en desarrollo
  const DEBUG_MODE = false;

  // Función de logging condicional
  function debugLog(...args) {
    if (DEBUG_MODE) {
      console.log('[PredictiveSearch]', ...args);
    }
  }

  // ============================================
  // SISTEMA DE PALABRAS PRIORITARIAS
  // ============================================
  // Palabras que tendrán prioridad en la búsqueda cuando hay pocos resultados
  const PRIORITY_KEYWORDS = ['Polera', 'Conjunto', 'Vestido', 'Abrigo'];

  // Mínimo de caracteres para activar la búsqueda prioritaria
  const MIN_PREFIX_LENGTH = 2;

  // Umbral de resultados: si hay menos de este número, se activa la búsqueda prioritaria
  const LOW_RESULTS_THRESHOLD = 3;

  /**
   * Busca si el término de búsqueda coincide con el prefijo de alguna palabra prioritaria
   * @param {string} searchTerm - Término de búsqueda del usuario
   * @returns {string|null} - Palabra prioritaria encontrada o null
   */
  function findPriorityKeyword(searchTerm) {
    if (!searchTerm || searchTerm.length < MIN_PREFIX_LENGTH) {
      return null;
    }

    const normalizedTerm = searchTerm.toLowerCase().trim();

    for (const keyword of PRIORITY_KEYWORDS) {
      const normalizedKeyword = keyword.toLowerCase();
      // Verificar si el término es un prefijo de la palabra prioritaria
      if (normalizedKeyword.startsWith(normalizedTerm)) {
        debugLog(`Prefijo prioritario detectado: "${searchTerm}" → "${keyword}"`);
        return keyword;
      }
    }

    return null;
  }

  // Funciones de utilidad para resaltado de texto (movidas fuera de la clase)
  function escapeRegExp(string) {
    // Escapar caracteres especiales de regex para prevenir XSS
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function resaltarTexto(textoBuscado, contenidoHTML) {
    const escapedText = escapeRegExp(textoBuscado);
    const regex = new RegExp(`(${escapedText})`, 'ig');
    return contenidoHTML.replace(regex, '<b>$1</b>');
  }

  function incluyeTexto(textoBuscado, textoCompleto) {
    return textoCompleto.toLowerCase().includes(textoBuscado.toLowerCase());
  }

  /**
   * Cuenta solo ítems del listado de productos (excluye queries, páginas, etc.).
   * El DOM del section usa #predictive-search-results-products-list para productos.
   */
  function countProductListItemsInMarkup(markup) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(markup, 'text/html');
    const list = doc.querySelector('#predictive-search-results-products-list');
    return list ? list.querySelectorAll('.predictive-search__list-item').length : 0;
  }

  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.activeVendor = this.getCurrentVendor();
      this.activeRequestId = 0;

      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        // Remove the results when they are no longer relevant for the new search term
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      // Update the term asap, don't wait for the predictive search query to finish loading
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);

      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        // Abort any pending search requests
        this.abortController.abort();
        this.abortController = new AbortController();
        this.close(true);
        return;
      }

    
      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);

      switch (event.code) {
        case 'ArrowUp':
        case 'ArrowDown':
        case 'Enter':
          event.preventDefault();
          break;
        default:
          return;
      }

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      const currentButtonText = searchForTextElement?.innerText;
      if (!currentButtonText) return;

      if (previousTerm.length > 0) {
        const matches = currentButtonText.match(new RegExp(escapeRegExp(previousTerm), 'g'));
        if (matches && matches.length > 1) {
          // The new term matches part of the button text and not just the search term
          return;
        }
      }

      const newButtonText =
        previousTerm.length > 0 ? currentButtonText.replace(previousTerm, newTerm) : currentButtonText;

      searchForTextElement.innerText = newButtonText;
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      // Filter out hidden elements (duplicated page and article resources)
      const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
        (element) => element.offsetParent !== null
      );

      if (moveUp && !selectedElement) return;

      // Usar indexOf() en lugar de while loop
      const selectedElementIndex = allVisibleElements.indexOf(selectedElement);

      this.statusElement.textContent = '';

      let activeElementIndex = 0;

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

      if (selectedOption) selectedOption.click();
    }

    /** Nueva señal para cancelar la petición anterior al iniciar otra (ahorra red). */
    resetFetchSignal() {
      this.abortController.abort();
      this.abortController = new AbortController();
    }

    getSearchResults(searchTerm) {
      const currentVendor = this.getCurrentVendor();
      const vendorChanged = this.activeVendor !== currentVendor;

      // Si el usuario cambió de marca, invalidamos caché y requests anteriores.
      if (vendorChanged) {
        this.activeVendor = currentVendor;
        this.activeRequestId += 1;
        this.abortController.abort();
        this.abortController = new AbortController();
        this.clearCachedResultsAcrossInstances();
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      const requestId = ++this.activeRequestId;
      const requestVendor = currentVendor;
      const requestTerm = searchTerm;
      const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

      const hasVendorFilter = !!currentVendor;

      debugLog('Vendor detectado:', currentVendor);
      debugLog(`Búsqueda: "${searchTerm}" - Vendor: ${currentVendor || 'Sin filtro'}`);

      // Crear clave de caché que incluya el vendor
      const cacheKey = hasVendorFilter ? `${queryKey}-${currentVendor}` : queryKey;

      if (this.cachedResults[cacheKey]) {
        // Evitar renderizar resultados desincronizados al cambiar marca/término.
        if (this.isRequestContextStale(requestId, requestTerm, requestVendor)) return;
        this.renderSearchResults(this.cachedResults[cacheKey], queryKey, false, requestTerm, requestVendor);
        return;
      }

      this.setLiveRegionLoadingState();
      this.resetFetchSignal();

      // Función para construir los parámetros de búsqueda
      const buildSearchParams = (includeVendor) => {
        const params = new URLSearchParams();
        params.append('q', searchTerm.trim());
        params.append('resources[type]', 'product');
        params.append('resources[product][limit]', '25');
        params.append('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');

        if (includeVendor && hasVendorFilter) {
          const vendorQuery = `${searchTerm.trim()} vendor:"${currentVendor}"`;
          params.set('q', vendorQuery);
          debugLog('Query con vendor:', vendorQuery);
        }

        params.append('section_id', 'predictive-search');
        return params;
      };

      // Función para procesar la respuesta de la API
      const processResponse = (text) => {
        const parser = new DOMParser();
        const parsedDocument = parser.parseFromString(text, 'text/html');
        const resultsContainer = parsedDocument.querySelector('#shopify-section-predictive-search');

        if (!resultsContainer) {
          return null;
        }

        return resultsContainer.innerHTML;
      };

      // Estrategia: primero buscar CON vendor, si no hay resultados buscar SIN vendor
      const searchWithVendor = hasVendorFilter;
      const searchUrl = `${routes.predictive_search_url}?${buildSearchParams(searchWithVendor).toString()}`;

      fetch(searchUrl, {
        signal: this.abortController.signal,
      })
        .then((response) => {
          if (!response.ok) {
            const error = new Error(response.status);
            this.close();
            throw error;
          }
          return response.text();
        })
        .then((text) => {
          if (this.isRequestContextStale(requestId, requestTerm, requestVendor)) return;

          const resultsMarkup = processResponse(text);

          if (!resultsMarkup) {
            debugLog('No se encontró contenedor de resultados');
            return;
          }

          const productCount = countProductListItemsInMarkup(resultsMarkup);
          debugLog(`Productos encontrados (con vendor filter: ${searchWithVendor}):`, productCount);

          // Si buscamos con vendor y no hay productos en la respuesta, hacer fallback sin filtro en la API
          // y filtrar por vendor en renderSearchResults (misma estrategia que antes).
          if (searchWithVendor && productCount === 0) {
            debugLog('No hay resultados con vendor, intentando fallback sin filtro...');

            const fallbackUrl = `${routes.predictive_search_url}?${buildSearchParams(false).toString()}`;

            return fetch(fallbackUrl, {
              signal: this.abortController.signal,
            })
              .then((response) => {
                if (!response.ok) throw new Error(response.status);
                return response.text();
              })
              .then((fallbackText) => {
                const fallbackMarkup = processResponse(fallbackText);

                if (!fallbackMarkup) {
                  debugLog('No hay resultados en fallback');
                  return;
                }

                debugLog('Productos en fallback (antes de filtrar):', countProductListItemsInMarkup(fallbackMarkup));

                // Guardar en caché y renderizar (el filtrado se hace en renderSearchResults)
                this.allPredictiveSearchInstances.forEach((instance) => {
                  instance.cachedResults[cacheKey] = fallbackMarkup;
                });
                if (this.isRequestContextStale(requestId, requestTerm, requestVendor)) return;
                this.renderSearchResults(fallbackMarkup, queryKey, false, requestTerm, requestVendor);
              });
          }

          // Hay resultados con el filtro de vendor, guardar y renderizar
          this.allPredictiveSearchInstances.forEach((instance) => {
            instance.cachedResults[cacheKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup, queryKey, false, requestTerm, requestVendor);
        })
        .catch((error) => {
          if (error?.name === 'AbortError' || error?.code === 20) {
            return;
          }
          debugLog('Error en búsqueda:', error.message);
          this.close();
          throw error;
        });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup, queryKey, isPrioritySearch = false, requestTerm = null, requestVendor = null) {
      if (!resultsMarkup || resultsMarkup.trim() === '') {
        return;
      }

      const currentVendor = requestVendor ?? this.getCurrentVendor();
      const hasVendorFilter = !!currentVendor;

      // Crear un elemento temporal para manipular el HTML
      const tempElement = document.createElement('div');
      tempElement.innerHTML = resultsMarkup;

      let productosVisibles = 0;

      // Si tenemos un vendor específico (diferente de Limonada), filtrar los resultados
      if (hasVendorFilter) {
        const productItems = tempElement.querySelectorAll('.predictive-search__list-item');

        productItems.forEach((item) => {
          const productVendorElement = item.querySelector('.predictive-search__item-vendor');
          const titleElement = item.querySelector('.predictive-search__item-heading');

          if (productVendorElement) {
            const productVendor = productVendorElement.textContent.trim();
            const titleText = titleElement?.textContent?.trim() || 'Sin título';

            debugLog(`Filtrando: "${titleText}" - Vendor: "${productVendor}" vs "${currentVendor}"`);

            // Normalización para comparación robusta
            const normalizedProductVendor = productVendor.toLowerCase().trim();
            const normalizedCurrentVendor = currentVendor.toLowerCase().trim();

            if (normalizedProductVendor !== normalizedCurrentVendor) {
              item.style.display = 'none';
            } else {
              productosVisibles++;
            }
          }
        });

        debugLog(`Productos visibles después del filtrado: ${productosVisibles}`);

        // ============================================
        // BÚSQUEDA PRIORITARIA: Si hay pocos resultados
        // ============================================
        if (!isPrioritySearch && productosVisibles < LOW_RESULTS_THRESHOLD) {
          const originalSearchTerm = queryKey.replace(/-/g, ' ');
          const priorityKeyword = findPriorityKeyword(originalSearchTerm);

          if (priorityKeyword) {
            debugLog(`Pocos resultados (${productosVisibles}). Activando búsqueda prioritaria con: "${priorityKeyword}"`);

            // Hacer búsqueda expandida con la palabra prioritaria
            this.performPrioritySearch(priorityKeyword, queryKey, currentVendor, requestTerm);
            return; // Salir, el nuevo render vendrá de performPrioritySearch
          }
        }

        // Si no hay productos visibles, mostrar un mensaje
        if (productosVisibles === 0) {
          const productGroup = tempElement.querySelector('.predictive-search__result-group');
          if (productGroup) {
            const noResultsMessage = document.createElement('p');
            noResultsMessage.className = 'predictive-search__no-results';
            noResultsMessage.textContent = `No se encontraron productos de ${currentVendor} para esta búsqueda.`;
            productGroup.appendChild(noResultsMessage);
          }
        }
      } else {
        // Sin filtro de vendor, contar todos los productos visibles
        productosVisibles = tempElement.querySelectorAll('.predictive-search__list-item').length;
      }

      // Resaltar el término buscado (texto real, no queryKey con guiones)
      const highlightTerm = (requestTerm && String(requestTerm).trim()) || queryKey.replace(/-/g, ' ');
      const productItems = tempElement.querySelectorAll('.predictive-search__list-item');

      productItems.forEach((item) => {
        const headingElement = item.querySelector('.predictive-search__item-heading');

        if (headingElement && highlightTerm.length > 0) {
          const headingText = headingElement.textContent.trim();
          if (incluyeTexto(highlightTerm, headingText)) {
            headingElement.innerHTML = resaltarTexto(highlightTerm, headingText);
          }
        }
      });

      this.predictiveSearchResults.innerHTML = tempElement.innerHTML;
      this.setAttribute('results', true);
      this.setAttribute('open', true);

      this.setLiveRegionResults();
      this.open();
    }

    /**
     * Realiza una búsqueda expandida usando la palabra prioritaria completa
     * @param {string} priorityKeyword - Palabra prioritaria completa (ej: "Vestido")
     * @param {string} originalQueryKey - Query key original para el cache
     * @param {string} currentVendor - Vendor actual para el filtro
     * @param {string|null} requestTerm - Término original de búsqueda
     */
    performPrioritySearch(priorityKeyword, originalQueryKey, currentVendor, requestTerm = null) {
      const requestId = ++this.activeRequestId;
      this.resetFetchSignal();

      const buildPriorityParams = (includeVendor) => {
        const params = new URLSearchParams();
        const q = includeVendor ? `${priorityKeyword} vendor:"${currentVendor}"` : priorityKeyword.trim();
        params.append('q', q);
        params.append('resources[type]', 'product');
        params.append('resources[product][limit]', '25');
        params.append('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');
        params.append('section_id', 'predictive-search');
        return params;
      };

      const priorityUrl = `${routes.predictive_search_url}?${buildPriorityParams(true).toString()}`;
      debugLog(`Búsqueda prioritaria URL: ${priorityUrl}`);

      fetch(priorityUrl, {
        signal: this.abortController.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          if (this.isRequestContextStale(requestId, requestTerm, currentVendor)) return;

          const parser = new DOMParser();
          const parsedDocument = parser.parseFromString(text, 'text/html');
          const resultsContainer = parsedDocument.querySelector('#shopify-section-predictive-search');

          if (!resultsContainer) {
            debugLog('No se encontró contenedor en búsqueda prioritaria');
            return;
          }

          let priorityMarkup = resultsContainer.innerHTML;
          let productCount = countProductListItemsInMarkup(priorityMarkup);

          debugLog(`Búsqueda prioritaria encontró ${productCount} productos para "${priorityKeyword}"`);

          if (productCount === 0) {
            const fallbackUrl = `${routes.predictive_search_url}?${buildPriorityParams(false).toString()}`;
            debugLog('Prioritaria sin productos con vendor, fallback sin filtro en query…');

            return fetch(fallbackUrl, { signal: this.abortController.signal })
              .then((r) => {
                if (!r.ok) throw new Error(r.status);
                return r.text();
              })
              .then((fbText) => {
                if (this.isRequestContextStale(requestId, requestTerm, currentVendor)) return;
                const fbDoc = parser.parseFromString(fbText, 'text/html');
                const fbContainer = fbDoc.querySelector('#shopify-section-predictive-search');
                if (!fbContainer) return;
                priorityMarkup = fbContainer.innerHTML;
                productCount = countProductListItemsInMarkup(priorityMarkup);
                debugLog(`Prioritaria fallback: ${productCount} productos antes de filtrar en cliente`);
                this.renderSearchResults(priorityMarkup, originalQueryKey, true, requestTerm, currentVendor);
              });
          }

          this.renderSearchResults(priorityMarkup, originalQueryKey, true, requestTerm, currentVendor);
        })
        .catch((error) => {
          if (error?.name === 'AbortError' || error?.code === 20) return;
          debugLog('Error en búsqueda prioritaria:', error.message);
        });
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');

      try {
        const liveRegionElement = this.querySelector('[data-predictive-search-live-region-count-value]');

        if (liveRegionElement) {
          const textContent = liveRegionElement.textContent;
          this.setLiveRegionText(textContent);
        } else {
          this.setLiveRegionText('Resultados de búsqueda disponibles');
        }
      } catch (error) {
        debugLog('Error en setLiveRegionResults:', error.message);
        this.setLiveRegionText('Resultados de búsqueda disponibles');
      }
    }

    getResultsMaxHeight() {
      this.resultsMaxHeight =
        window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }

    open() {
      const maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight();

      try {
        this.predictiveSearchResults.style.maxHeight = `${maxHeight}px`;
        this.setAttribute('open', true);
        this.input.setAttribute('aria-expanded', true);
        this.isOpen = true;
      } catch (error) {
        debugLog('Error al abrir resultados:', error.message);
      }
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute('style');

      // Clear the results content to ensure clean slate
      if (clearSearchTerm && this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
    }

    /**
     * Vendor de la página actual: prioridad a data-context-vendor (Liquid por documento),
     * así cada pestaña mantiene la marca del HTML cargado y no la última escritura en localStorage.
     */
    getCurrentVendor() {
      const fromDom = this.getAttribute('data-context-vendor');
      if (typeof fromDom === 'string' && fromDom.trim().length > 0) {
        return fromDom.trim();
      }
      const vendor = localStorage.getItem('brand');
      return vendor || null;
    }

    clearCachedResultsAcrossInstances() {
      this.allPredictiveSearchInstances.forEach((instance) => {
        instance.cachedResults = {};
      });
    }

    isRequestContextStale(requestId, requestTerm, requestVendor) {
      const liveTerm = this.getQuery();
      const liveVendor = this.getCurrentVendor();
      return requestId !== this.activeRequestId || requestTerm !== liveTerm || requestVendor !== liveVendor;
    }
  }

  // Define selectCallback if it doesn't exist to prevent errors in search page
  if (typeof window.selectCallback === 'undefined') {
    window.selectCallback = function (variant, selector) {
      if (variant && variant.available) {
        const addToCartButtons = document.querySelectorAll('.add-to-cart');
        addToCartButtons.forEach(button => {
          button.disabled = false;
          button.classList.remove('disabled');
          button.textContent = window.variantStrings?.addToCart || 'Agregar al carrito';
        });
      } else {
        const addToCartButtons = document.querySelectorAll('.add-to-cart');
        addToCartButtons.forEach(button => {
          button.disabled = true;
          button.classList.add('disabled');
          button.textContent = window.variantStrings?.soldOut || 'Sin stock';
        });
      }
    };
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();